import { useEffect, useRef, type ReactNode } from "react";
import { Refine } from "@refinedev/core";
import { useNotificationProvider } from "@refinedev/antd";
import routerProvider from "@refinedev/react-router-v6";
import dataProvider from "@refinedev/simple-rest";
import {
  ApiOutlined,
  AppstoreOutlined,
  AuditOutlined,
  BarChartOutlined,
  ClockCircleOutlined,
  CloudDownloadOutlined,
  CoffeeOutlined,
  CreditCardOutlined,
  DashboardOutlined,
  DollarCircleOutlined,
  EuroOutlined,
  ExperimentOutlined,
  HomeOutlined,
  InboxOutlined,
  MobileOutlined,
  PercentageOutlined,
  QrcodeOutlined,
  SettingOutlined,
  ShoppingCartOutlined,
  ShoppingOutlined,
  TagsOutlined,
  TeamOutlined,
  UploadOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { App as AntdApp, ConfigProvider } from "antd";
import deDE from "antd/locale/de_DE";
import dayjs from "dayjs";
import "dayjs/locale/de";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";

// Switch dayjs to German so AntD pickers render month/day-of-week names
// in German and use German first-day-of-week. The per-DatePicker
// `format="DD.MM.YYYY"` overrides ensure the on-screen value is the
// unambiguous European order regardless of any other defaults.
dayjs.locale("de");

import { AppShell } from "./layout/AppShell";
import { ProtectedRoute } from "./layout/ProtectedRoute";
import { CatalogWorkspace } from "./pages/catalog/CatalogWorkspace";
import { FinanceCashPage } from "./pages/finance/FinanceCashPage";
import { BackupsPage } from "./pages/BackupsPage";
import { ErpSettingsPage } from "./pages/ErpSettingsPage";
// Spec §11.9 replaced the legacy /pages/EstimationPage.tsx with this
// EB-Gamma-Poisson page under /pages/stock/EstimationPage.tsx. The
// legacy file was removed in the §15 cleanup.
import { EstimationPage } from "./pages/stock/EstimationPage";
import { LoginPage } from "./pages/LoginPage";
import { ConsumptionPage } from "./pages/ConsumptionPage";
import { DashboardPage } from "./pages/DashboardPage";
import { ImportPage } from "./pages/ImportPage";
import { PaymentMethodsPage } from "./pages/PaymentMethodsPage";
import { PendingApprovalPage } from "./pages/PendingApprovalPage";
import { SettlementsPage } from "./pages/SettlementsPage";
import { SetupPage } from "./pages/SetupPage";
import { StockWorkspace } from "./pages/stock/StockWorkspace";
import ProcurementPage from "./pages/stock/ProcurementPage";
import ProcurementDetailPage from "./pages/stock/ProcurementDetailPage";
import StocktakeListPage from "./pages/stock/StocktakeListPage";
import StocktakeDetailPage from "./pages/stock/StocktakeDetailPage";
import ExpensesPage from "./pages/stock/ExpensesPage";
import PfandValuesPage from "./pages/settings/PfandValuesPage";
import VatRatesPage from "./pages/settings/VatRatesPage";
import ExpenseCategoriesPage from "./pages/settings/ExpenseCategoriesPage";
import EstimationExclusionsPage from "./pages/settings/EstimationExclusionsPage";
import MiscSettingsPage from "./pages/settings/MiscSettingsPage";
import UserScannerSettingsPage from "./pages/settings/UserScannerSettingsPage";
import SumupSettingsPage from "./pages/settings/SumupSettingsPage";
import SumupTransactionsPage from "./pages/settings/SumupTransactionsPage";
import { UserDetailPage } from "./pages/UserDetailPage";
import { UsersPage } from "./pages/UsersPage";
import { VouchersPage } from "./pages/vouchers/VouchersPage";
import { VoucherPrintPage } from "./pages/vouchers/VoucherPrintPage";
import { CorrectionReasonsPage } from "./pages/CorrectionReasonsPage";
import { VenueSettingsPage } from "./pages/VenueSettingsPage";
import AdminUsersPage from "./pages/settings/AdminUsersPage";
import OidcSettingsPage from "./pages/settings/OidcSettingsPage";
import RolesMatrixPage from "./pages/settings/RolesMatrixPage";
import { PosPage } from "./pages/PosPage";
import { PosCupsPage } from "./pages/pos/PosCupsPage";
import { PosRecipesPage } from "./pages/pos/PosRecipesPage";
import { PosCustomerDisplayPage } from "./pages/pos/PosCustomerDisplayPage";
import { PosStationsPage } from "./pages/pos/PosStationsPage";
import { PosSessionsPage } from "./pages/pos/PosSessionsPage";
import { PosCorrectionsPage } from "./pages/pos/PosCorrectionsPage";
import { PosReportsPage } from "./pages/pos/PosReportsPage";
import { DarkModeProvider } from "./theme/DarkModeProvider";
import { buildTheme } from "./theme/tokens";
import { useDarkMode } from "./theme/useDarkMode";
import { useCurrentUser } from "./hooks/useCurrentUser";

const apiUrl = "/api";

/** Owner = env-superadmin OR an admin_user with role "owner". Mirrors the
 * backend ``require_role(AdminRole.OWNER)`` gate (superadmin always passes,
 * "owner" is the top admin role). */
function isOwner(user: { is_superadmin?: boolean; role: string | null } | null) {
  return !!user && (user.is_superadmin === true || user.role === "owner");
}

/**
 * Refine resources drive the sidebar nav. Each new page added by future
 * phases registers itself here and shows up automatically — no edits to
 * the layout needed. ``meta.parent`` nests entries under a parent group.
 */
const resources = [
  {
    name: "dashboard",
    list: "/dashboard",
    meta: { label: "Dashboard", icon: <DashboardOutlined /> },
  },
  {
    name: "consumption",
    list: "/analytics/consumption",
    meta: { label: "Consumption", icon: <BarChartOutlined /> },
  },
  {
    name: "cash-drawer",
    list: "/finance/cash",
    meta: { label: "Cash drawer", icon: <WalletOutlined /> },
  },
  // Users is a single top-level button now that CSV Import lives under
  // Settings — no submenu to expand, clicking the sidebar entry goes
  // straight to the list.
  {
    name: "users",
    list: "/users",
    meta: { label: "Users", icon: <TeamOutlined /> },
  },
  {
    name: "catalog",
    list: "/catalog",
    meta: { label: "Catalog", icon: <ShoppingOutlined /> },
  },
  {
    name: "stock",
    meta: { label: "Finance", icon: <BarChartOutlined /> },
  },
  {
    name: "refills",
    list: "/stock/procurement",
    meta: { label: "Procurement", icon: <InboxOutlined />, parent: "stock" },
  },
  {
    name: "expenses",
    list: "/stock/expenses",
    meta: { label: "Expenses", icon: <DollarCircleOutlined />, parent: "stock" },
  },
  {
    name: "stocktakes",
    list: "/stock/stocktakes",
    meta: { label: "Stocktakes", icon: <AuditOutlined />, parent: "stock" },
  },
  {
    name: "estimation",
    list: "/stock/estimation",
    meta: { label: "Order AI 4.0", icon: <BarChartOutlined />, parent: "stock" },
  },
  {
    name: "settlements",
    list: "/settlements",
    meta: { label: "Settlements", icon: <DollarCircleOutlined /> },
  },
  {
    name: "vouchers",
    list: "/vouchers",
    meta: { label: "Vouchers", icon: <QrcodeOutlined /> },
  },
  // Point-of-sale group (Option-1 IA). The sell screen stays menu-less at
  // ``/pos``; shared config (SumUp reader, on-site pfand, comp cats) lives in
  // Settings. Server-side: Stations = Owner, Sessions = Office+, the rest POS.
  {
    name: "pos",
    meta: { label: "POS", icon: <ShoppingCartOutlined /> },
  },
  {
    name: "pos-sell",
    list: "/pos",
    meta: { label: "Sell screen", icon: <ShoppingCartOutlined />, parent: "pos" },
  },
  {
    name: "pos-stations",
    list: "/pos/stations",
    meta: { label: "Stations", icon: <HomeOutlined />, parent: "pos" },
  },
  {
    name: "pos-cups",
    list: "/pos/cups",
    meta: { label: "Cups & deposits", icon: <CoffeeOutlined />, parent: "pos" },
  },
  {
    name: "pos-recipes",
    list: "/pos/recipes",
    meta: { label: "Recipes", icon: <ExperimentOutlined />, parent: "pos" },
  },
  {
    name: "pos-sessions",
    list: "/pos/sessions",
    meta: { label: "Sessions", icon: <WalletOutlined />, parent: "pos" },
  },
  {
    name: "pos-corrections",
    list: "/pos/corrections",
    meta: { label: "Corrections", icon: <AuditOutlined />, parent: "pos" },
  },
  {
    name: "pos-reports",
    list: "/pos/reports",
    meta: { label: "Reports", icon: <BarChartOutlined />, parent: "pos" },
  },
  {
    name: "settings",
    meta: { label: "Settings", icon: <SettingOutlined /> },
  },
  // CSV Import lives under Settings in the menu. URL stays at
  // ``/users/import`` so existing bookmarks / docs don't break.
  {
    name: "import",
    list: "/users/import",
    meta: { label: "CSV Import", icon: <UploadOutlined />, parent: "settings" },
  },
  {
    name: "correction-reasons",
    list: "/settings/correction-reasons",
    meta: {
      label: "Correction reasons",
      icon: <AuditOutlined />,
      parent: "settings",
    },
  },
  {
    name: "payment-methods",
    list: "/settings/payment-methods",
    meta: {
      label: "Payment methods",
      icon: <CreditCardOutlined />,
      parent: "settings",
    },
  },
  {
    name: "expense-categories",
    list: "/settings/expense-categories",
    meta: {
      label: "Expense categories",
      icon: <TagsOutlined />,
      parent: "settings",
    },
  },
  {
    name: "pfand-values",
    list: "/settings/pfand-values",
    meta: {
      label: "Pfand values",
      icon: <EuroOutlined />,
      parent: "settings",
    },
  },
  {
    name: "vat-rates",
    list: "/settings/vat-rates",
    meta: {
      label: "VAT rates",
      icon: <PercentageOutlined />,
      parent: "settings",
    },
  },
  {
    name: "estimation-exclusions",
    list: "/settings/estimation-exclusions",
    meta: {
      label: "Estimation exclusions",
      icon: <ClockCircleOutlined />,
      parent: "settings",
    },
  },
  {
    name: "misc-settings",
    list: "/settings/misc",
    meta: {
      label: "Operational & volunteer",
      icon: <AppstoreOutlined />,
      parent: "settings",
    },
  },
  {
    name: "user-scanner",
    list: "/settings/user-scanner",
    meta: {
      label: "User scanner",
      icon: <QrcodeOutlined />,
      parent: "settings",
    },
  },
  // SumUp card settlement — Owner-only. Appended to ``resources`` only
  // when the current user is an Owner (see ``buildResources`` below) so
  // the nav entry is hidden for non-owners; the route is also guarded.
  {
    name: "sumup",
    list: "/settings/sumup",
    meta: {
      label: "SumUp settlement",
      icon: <MobileOutlined />,
      parent: "settings",
    },
  },
  {
    name: "sumup-transactions",
    list: "/settings/sumup/transactions",
    meta: {
      label: "SumUp transactions",
      icon: <CreditCardOutlined />,
      parent: "settings",
    },
  },
  {
    name: "backups",
    list: "/settings/backups",
    meta: {
      label: "Backups",
      icon: <CloudDownloadOutlined />,
      parent: "settings",
    },
  },
  {
    name: "erp",
    list: "/settings/erp",
    meta: {
      label: "Snipe-IT integration",
      icon: <ApiOutlined />,
      parent: "settings",
    },
  },
  {
    name: "venue",
    list: "/settings/venue",
    meta: { label: "Venue", icon: <HomeOutlined />, parent: "settings" },
  },
  // Admin operators + SSO are Owner-only (appended for Owners in buildResources).
  {
    name: "admin-users",
    list: "/settings/admin-users",
    meta: { label: "Admin users", icon: <TeamOutlined />, parent: "settings" },
  },
  {
    name: "sso",
    list: "/settings/sso",
    meta: { label: "Single Sign-On", icon: <ApiOutlined />, parent: "settings" },
  },
  {
    name: "roles",
    list: "/settings/roles",
    meta: { label: "Roles & access", icon: <AuditOutlined />, parent: "settings" },
  },
];

// Owner-only resource names — hidden from the nav for non-owners. The
// matching routes are also guarded by ``OwnerRoute`` so a deep link
// can't bypass the nav.
const OWNER_ONLY_RESOURCES = new Set([
  "sumup",
  "sumup-transactions",
  "admin-users",
  "sso",
]);

/** Filter the static resource list down to what the current user may see.
 * Non-owners never get the SumUp nav entry. */
function buildResources(owner: boolean): typeof resources {
  if (owner) return resources;
  return resources.filter((r) => !OWNER_ONLY_RESOURCES.has(r.name));
}

/** Route guard: render children only for Owners; non-owners are bounced to
 * the dashboard. Mirrors the backend Owner-only endpoint protection. */
const OwnerRoute = ({ children }: { children: ReactNode }) => {
  const { status, user } = useCurrentUser();
  if (status === "loading") return null;
  return isOwner(user) ? <>{children}</> : <Navigate to="/dashboard" replace />;
};

const RedirectArticleToCatalog = () => {
  const { id } = useParams();
  return <Navigate to={`/catalog/articles/${id}`} replace />;
};

const ThemedRefine = ({ children }: { children: ReactNode }) => {
  const { mode } = useDarkMode();
  return (
    <ConfigProvider locale={deDE} theme={buildTheme(mode)}>
      <AntdApp>{children}</AntdApp>
    </ConfigProvider>
  );
};

// Public, shell-less pages a user can be on while NOT yet authenticated.
// Crossing OUT of one of these into the app means a session was just
// established — re-check identity so owner-aware nav resources refresh.
const AUTH_PAGES = ["/login", "/setup", "/pending-approval"];

// Refine drives the sidebar nav from ``resources``; compute the owner-aware
// list here so Owner-only entries (SumUp) stay out of the nav for everyone
// else. The matching routes are still guarded by ``OwnerRoute``.
const ResourcedRefine = ({ children }: { children: ReactNode }) => {
  const { user, refresh } = useCurrentUser();
  const { pathname } = useLocation();

  // ResourcedRefine mounts ABOVE the router, so it never remounts on a
  // client-side navigate(). Its ``useCurrentUser`` therefore keeps whatever
  // it resolved at first mount — e.g. ``anonymous`` from a logged-out cold
  // load — even after a password/barcode login navigates into the app. That
  // left owner-only nav entries (SumUp settings/transactions, Admin users,
  // SSO) filtered out until a hard reload. Re-check identity whenever we
  // cross from an auth page into the app so the menu reflects the freshly
  // authenticated session. (The SSO path does a full redirect, so it remounts
  // and never hit this; normal username/barcode login did.)
  const prevPath = useRef(pathname);
  useEffect(() => {
    const cameFromAuthPage = AUTH_PAGES.includes(prevPath.current);
    prevPath.current = pathname;
    if (cameFromAuthPage && !AUTH_PAGES.includes(pathname)) {
      void refresh();
    }
  }, [pathname, refresh]);

  return (
    <Refine
      dataProvider={dataProvider(apiUrl)}
      routerProvider={routerProvider}
      notificationProvider={useNotificationProvider}
      resources={buildResources(isOwner(user))}
      options={{
        disableTelemetry: true,
        warnWhenUnsavedChanges: true,
      }}
    >
      {children}
    </Refine>
  );
};

export const App = () => (
  // Future flags silence the v6 → v7 deprecation warnings React Router
  // logs on every page load. Both behaviours are forwards-compatible
  // and what we'd want when v7 lands.
  <BrowserRouter
    future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
  >
    <DarkModeProvider>
      <ThemedRefine>
        <ResourcedRefine>
          <Routes>
            {/* Public routes — no shell, no auth guard */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/setup" element={<SetupPage />} />
            <Route path="/pending-approval" element={<PendingApprovalPage />} />

            {/* Authenticated but shell-less — the print sheet renders clean. */}
            <Route
              path="/vouchers/batch/:creationId/print"
              element={
                <ProtectedRoute>
                  <VoucherPrintPage />
                </ProtectedRoute>
              }
            />

            {/* Customer-facing display — PUBLIC, no shell, no login. Authed
                only by the signed station token in the URL; read-only. */}
            <Route path="/pos/display" element={<PosCustomerDisplayPage />} />

            {/* Scoped point-of-sale surface — menu-less; the ProtectedRoute
                keeps pos-only users here and out of the full app shell. */}
            <Route
              path="/pos"
              element={
                <ProtectedRoute>
                  <PosPage />
                </ProtectedRoute>
              }
            />

            {/* Authenticated routes — guarded + wrapped in the AppShell */}
            <Route
              element={
                <ProtectedRoute>
                  <AppShell>
                    <Outlet />
                  </AppShell>
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route
                path="/analytics/consumption"
                element={<ConsumptionPage />}
              />
              <Route path="/users" element={<UsersPage />} />
              <Route path="/users/import" element={<ImportPage />} />
              <Route path="/users/:id" element={<UserDetailPage />} />
              <Route path="/catalog" element={<CatalogWorkspace />} />
              <Route path="/catalog/articles/:articleId" element={<CatalogWorkspace />} />
              <Route path="/catalog/categories/:categoryId" element={<CatalogWorkspace />} />
              <Route path="/catalog/groups/:groupId" element={<CatalogWorkspace />} />
              {/* Legacy deep-link redirects. */}
              <Route path="/articles" element={<Navigate to="/catalog" replace />} />
              <Route
                path="/articles/:id"
                element={<RedirectArticleToCatalog />}
              />
              <Route path="/categories" element={<Navigate to="/catalog" replace />} />
              <Route path="/product-groups" element={<Navigate to="/catalog" replace />} />
              <Route path="/stock" element={<StockWorkspace />}>
                {/* Overview retired — its read-only stock view moved into
                    the Catalog (Stock preset). Bare /stock lands on
                    Procurement. */}
                <Route
                  index
                  element={<Navigate to="/stock/procurement" replace />}
                />
                <Route path="procurement" element={<ProcurementPage />} />
                <Route
                  path="procurement/:id"
                  element={<ProcurementDetailPage />}
                />
                <Route path="stocktakes" element={<StocktakeListPage />} />
                <Route path="stocktakes/:id" element={<StocktakeDetailPage />} />
                <Route path="expenses" element={<ExpensesPage />} />
                <Route path="estimation" element={<EstimationPage />} />
              </Route>
              {/* Legacy deep-link redirects into the new workspace. */}
              <Route
                path="/stock-overview"
                element={<Navigate to="/stock/procurement" replace />}
              />
              <Route
                path="/refills"
                element={<Navigate to="/stock/procurement" replace />}
              />
              <Route
                path="/stocktakes"
                element={<Navigate to="/stock/stocktakes" replace />}
              />
              <Route path="/finance/cash" element={<FinanceCashPage />} />
              <Route path="/settlements" element={<SettlementsPage />} />
              <Route path="/vouchers" element={<VouchersPage />} />
              {/* POS management pages (the menu-less sell screen + customer
                  display are separate, shell-less routes above). */}
              <Route path="/pos/stations" element={<PosStationsPage />} />
              <Route path="/pos/cups" element={<PosCupsPage />} />
              <Route path="/pos/recipes" element={<PosRecipesPage />} />
              <Route path="/pos/sessions" element={<PosSessionsPage />} />
              <Route path="/pos/corrections" element={<PosCorrectionsPage />} />
              <Route path="/pos/reports" element={<PosReportsPage />} />
              <Route
                path="/settings/payment-methods"
                element={<PaymentMethodsPage />}
              />
              <Route path="/settings/venue" element={<VenueSettingsPage />} />
              <Route path="/settings/backups" element={<BackupsPage />} />
              <Route path="/settings/erp" element={<ErpSettingsPage />} />
              <Route
                path="/settings/correction-reasons"
                element={<CorrectionReasonsPage />}
              />
              <Route
                path="/settings/expense-categories"
                element={<ExpenseCategoriesPage />}
              />
              <Route
                path="/settings/pfand-values"
                element={<PfandValuesPage />}
              />
              <Route
                path="/settings/vat-rates"
                element={<VatRatesPage />}
              />
              <Route
                path="/settings/estimation-exclusions"
                element={<EstimationExclusionsPage />}
              />
              <Route path="/settings/misc" element={<MiscSettingsPage />} />
              <Route
                path="/settings/user-scanner"
                element={<UserScannerSettingsPage />}
              />
              {/* Owner-only — non-owners are redirected to the dashboard. */}
              <Route
                path="/settings/sumup"
                element={
                  <OwnerRoute>
                    <SumupSettingsPage />
                  </OwnerRoute>
                }
              />
              <Route
                path="/settings/sumup/transactions"
                element={
                  <OwnerRoute>
                    <SumupTransactionsPage />
                  </OwnerRoute>
                }
              />
              <Route
                path="/settings/admin-users"
                element={
                  <OwnerRoute>
                    <AdminUsersPage />
                  </OwnerRoute>
                }
              />
              <Route
                path="/settings/sso"
                element={
                  <OwnerRoute>
                    <OidcSettingsPage />
                  </OwnerRoute>
                }
              />
              <Route path="/settings/roles" element={<RolesMatrixPage />} />
              <Route
                path="/estimation"
                element={<Navigate to="/stock/estimation" replace />}
              />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Route>
          </Routes>
        </ResourcedRefine>
      </ThemedRefine>
    </DarkModeProvider>
  </BrowserRouter>
);
